/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import { observer } from 'mobx-react-lite';
import { MenuItem, MenuSeparator } from '@veeam/components';

import { removeEdgeSeparator } from '../../helpers';
// eslint-disable-next-line import/no-cycle
import { Submenu } from '../Submenu';
import { useContextMenuStore } from '../../store';

import type { MenuItemsProps } from '../../interfaces';

export const MenuItems = observer(React.forwardRef(function MenuItems(
    { parent }: MenuItemsProps,
    ref: React.RefObject<HTMLDivElement>
) {
    const menuStore = useContextMenuStore();

    let menuItems = menuStore.menuItems;

    if (parent) {
        menuItems = menuStore.findMenuItem(item => item.id === parent.id).items;
    }

    if (!menuItems || menuItems.length === 0 || menuItems.every(x => x.hidden)) {
        return null;
    }

    return (
        <div ref={ref}>
            {menuItems
                .filter(removeEdgeSeparator)
                .map((menuItem, index) => {
                    if (menuItem.hidden) {
                        return null;
                    }

                    if (menuItem.separator) {
                        return (
                            <MenuSeparator
                                data-tid={`command-menu-item-separator-${index}`}
                                key={`command-menu-item-separator-${index}`}
                                onMouseEnter={() => {
                                    menuStore.hideOtherSubmenus(menuItem);
                                }}
                            />
                        );
                    }

                    if (menuItem.items && menuItem.items.length) {
                        return (
                            <Submenu
                                key={menuItem.id}
                                id={menuItem.id}
                                text={menuItem.text}
                                isActive={menuItem.isActive}
                                disabled={menuItem.disabled || menuItem.items.every(x => x.disabled || x.separator)}
                                hidden={menuItem.hidden || menuItem.items.every(x => x.hidden || x.separator)}
                                icon={menuItem.icon}
                                items={menuItem.items}
                                onClick={menuItem.onClick}
                            />
                        );
                    }

                    return (
                        <MenuItem
                            key={menuItem.id}
                            data-tid={`command-menu-item-${menuItem.id}`}
                            isActive={menuItem.isActive}
                            disabled={menuItem.disabled}
                            icon={menuItem.icon}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();

                                menuStore.hideMenu();

                                if (!menuItem.disabled && menuItem.onClick) {
                                    menuItem.onClick(event);
                                }
                            }}
                            onMouseEnter={() => {
                                menuStore.hideOtherSubmenus(menuItem);
                            }}
                        >
                            {menuItem.text}
                        </MenuItem>
                    );
                })}
        </div>
    );
}));
